import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";

const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

export const getAllMovements = createAsyncThunk(
  "movements/getAll",
  async ({ selectedAccount, q = "", archive = false }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ account_id: selectedAccount, archive });
      if (q) params.append("q", q);
      const response = await axios.get(`${apiBaseUrl}/movements?${params}`, {
        headers: authHeader(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

const buildFormData = (movement, thumbnailFile) => {
  const fd = new FormData();
  Object.entries(movement).forEach(([k, v]) => {
    if (v == null) return;
    if (Array.isArray(v)) {
      if (v.length === 0) {
        fd.append(`movement[${k}][]`, "");
      } else {
        v.forEach((item) => fd.append(`movement[${k}][]`, item));
      }
    } else {
      fd.append(`movement[${k}]`, v);
    }
  });
  if (thumbnailFile === null) {
    fd.append("movement[thumbnail]", "null");
  } else if (thumbnailFile instanceof File) {
    fd.append("movement[thumbnail]", thumbnailFile);
  }
  return fd;
};

export const createMovement = createAsyncThunk(
  "movements/create",
  async ({ movement, thumbnailFile, selectedAccount }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/movements?account_id=${selectedAccount}`,
        buildFormData(movement, thumbnailFile),
        { headers: { "Content-Type": "multipart/form-data", ...authHeader() } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const updateMovement = createAsyncThunk(
  "movements/update",
  async ({ id, movement, thumbnailFile, selectedAccount }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${apiBaseUrl}/movements/${id}?account_id=${selectedAccount}`,
        buildFormData(movement, thumbnailFile),
        { headers: { "Content-Type": "multipart/form-data", ...authHeader() } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const getMovement = createAsyncThunk(
  "movements/getOne",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/movements/${id}?account_id=${selectedAccount}`,
        { headers: authHeader() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const deleteMovement = createAsyncThunk(
  "movements/delete",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.delete(
        `${apiBaseUrl}/movements/${id}?account_id=${selectedAccount}`,
        { headers: authHeader() }
      );
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const archiveMovement = createAsyncThunk(
  "movements/archive",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.post(
        `${apiBaseUrl}/movements/${id}/archive?account_id=${selectedAccount}`,
        {},
        { headers: authHeader() }
      );
      toast.success("Movement archived successfully");
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const unarchiveMovement = createAsyncThunk(
  "movements/unarchive",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    try {
      await axios.post(
        `${apiBaseUrl}/movements/${id}/unarchive?account_id=${selectedAccount}`,
        {},
        { headers: authHeader() }
      );
      toast.success("Movement unarchived successfully");
      return { id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

const movementSlice = createSlice({
  name: "movements",
  initialState: {
    list:            [],
    currentMovement: null,
    isLoading:       false,
    error:           null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllMovements.pending,   (state) => { state.isLoading = true; state.error = null; })
      .addCase(getAllMovements.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list      = action.payload;
      })
      .addCase(getAllMovements.rejected,  (state, action) => {
        state.isLoading = false;
        state.error     = action.payload;
      })
      .addCase(getMovement.pending,   (state) => { state.isLoading = true; state.error = null; })
      .addCase(getMovement.fulfilled, (state, action) => {
        state.isLoading       = false;
        state.currentMovement = action.payload;
      })
      .addCase(getMovement.rejected,  (state, action) => {
        state.isLoading = false;
        state.error     = action.payload;
      })
      .addCase(createMovement.fulfilled, (state, action) => {
        state.list = [...state.list, action.payload];
      })
      .addCase(updateMovement.fulfilled, (state, action) => {
        state.list = state.list.map((t) => t.id === action.payload.id ? action.payload : t);
      })
      .addCase(deleteMovement.fulfilled, (state, action) => {
        state.list = state.list.filter((t) => t.id !== action.payload.id);
      })
      .addCase(archiveMovement.fulfilled, (state, action) => {
        state.list = state.list.filter((t) => t.id !== action.payload.id);
      })
      .addCase(unarchiveMovement.fulfilled, (state, action) => {
        state.list = state.list.filter((t) => t.id !== action.payload.id);
      });
  },
});

export default movementSlice.reducer;
